import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { resignationAPI } from '../../services';
import { 
  FileText, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Calendar,
  Send,
  ArrowLeft,
  X
} from 'lucide-react';

const EmployeeResignation = () => {
  const { user } = useAuth();
  const [myResignation, setMyResignation] = useState(null);
  const [resignationHistory, setResignationHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    resignationType: 'voluntary',
    reason: '',
    lastWorkingDate: '',
    noticePeriodDays: 30,
    feedback: ''
  });

  // Auto-calculate last working date when notice period changes
  useEffect(() => {
    if (formData.noticePeriodDays && showForm) {
      const today = new Date();
      const calculatedDate = new Date(today);
      calculatedDate.setDate(calculatedDate.getDate() + formData.noticePeriodDays);
      
      // Format date as YYYY-MM-DD for input field
      const formattedDate = calculatedDate.toISOString().split('T')[0];
      setFormData(prev => ({ ...prev, lastWorkingDate: formattedDate }));
    }
  }, [formData.noticePeriodDays, showForm]);

  useEffect(() => {
    fetchMyResignation();
  }, []);

  const fetchMyResignation = async () => {
    try {
      setLoading(true);
      const response = await resignationAPI.getMyResignation();
      const resignationData = response?.data?.resignation;
      const historyData = response?.data?.resignationHistory || [];
      setMyResignation(resignationData || null);
      setResignationHistory(historyData);
    } catch (error) {
      console.error('Error fetching resignation:', error);
      // If no resignation found or error, set to null
      setMyResignation(null);
      setResignationHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.reason.trim()) {
      setErrorMessage('Please provide a reason for resignation');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    if (!formData.lastWorkingDate) {
      setErrorMessage('Please select last working date');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    try {
      await resignationAPI.submitResignation(formData);
      setSuccessMessage('Resignation submitted successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowForm(false);
      fetchMyResignation();
    } catch (error) {
      console.error('Error submitting resignation:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to submit resignation');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const handleWithdraw = async () => {
    if (!withdrawReason.trim()) {
      setErrorMessage('Please provide a reason for withdrawal');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    setWithdrawing(true);
    try {
      await resignationAPI.withdrawResignation(myResignation._id, { withdrawnReason: withdrawReason });
      setSuccessMessage('Resignation withdrawn successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowWithdrawModal(false);
      setWithdrawReason('');
      fetchMyResignation();
    } catch (error) {
      console.error('Error withdrawing resignation:', error);
      setErrorMessage('Failed to withdraw resignation');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setWithdrawing(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending Manager Review' },
      approved_by_reporter: { color: 'bg-blue-100 text-blue-800', icon: AlertCircle, label: 'Pending Admin Approval' },
      rejected_by_reporter: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Rejected by Manager' },
      approved_by_admin: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Approved' },
      rejected_by_admin: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Rejected by Admin' },
      withdrawn: { color: 'bg-gray-100 text-gray-800', icon: XCircle, label: 'Withdrawn' },
      completed: { color: 'bg-purple-100 text-purple-800', icon: CheckCircle, label: 'Completed' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        <Icon className="w-4 h-4 mr-2" />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (showForm) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <button
          onClick={() => setShowForm(false)}
          className="mb-6 flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </button>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Submit Resignation</h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resignation Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.resignationType}
                onChange={(e) => setFormData({ ...formData, resignationType: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="voluntary">Voluntary Resignation</option>
                <option value="mutual_separation">Mutual Separation</option>
                <option value="retirement">Retirement</option>
                <option value="end_of_contract">End of Contract</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Resignation <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                rows="4"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Please provide your reason for resignation..."
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notice Period <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.noticePeriodDays}
                  onChange={(e) => setFormData({ ...formData, noticePeriodDays: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="15">15 Days</option>
                  <option value="30">30 Days</option>
                  <option value="60">60 Days</option>
                  <option value="90">90 Days</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Working Date <span className="text-red-500">*</span>
                  <span className="text-xs text-gray-500 ml-2">(Auto-calculated)</span>
                </label>
                <input
                  type="date"
                  value={formData.lastWorkingDate}
                  onChange={(e) => setFormData({ ...formData, lastWorkingDate: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Calculated from today + notice period. You can adjust if needed.
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Feedback (Optional)
              </label>
              <textarea
                value={formData.feedback}
                onChange={(e) => setFormData({ ...formData, feedback: e.target.value })}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Any additional feedback you'd like to share..."
              />
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex">
                <AlertCircle className="w-5 h-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium mb-1">Important Notice</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Your resignation will be reviewed by your reporting manager and admin</li>
                    <li>You can withdraw your resignation before it's approved</li>
                    <li>Please ensure all pending work is completed before your last working day</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                type="submit"
                className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center font-medium"
              >
                <Send className="w-5 h-5 mr-2" />
                Submit Resignation
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (!myResignation || !myResignation._id) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Active Resignation</h2>
          <p className="text-gray-600 mb-8">You haven't submitted any resignation request yet.</p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white py-3 px-8 rounded-lg hover:bg-blue-700 transition-colors font-medium inline-flex items-center"
          >
            <FileText className="w-5 h-5 mr-2" />
            Submit Resignation
          </button>
        </div>

        {/* Resignation History */}
        {resignationHistory.length > 0 && (
          <div className="mt-6 bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">Resignation History</h3>
              <p className="text-sm text-gray-600">Your previous resignation requests</p>
            </div>
            <div className="divide-y divide-gray-200">
              {resignationHistory.map((item) => (
                <div key={item._id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          item.status === 'withdrawn' 
                            ? 'bg-gray-100 text-gray-800'
                            : item.status === 'completed'
                            ? 'bg-purple-100 text-purple-800'
                            : item.status.includes('rejected')
                            ? 'bg-red-100 text-red-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {item.status === 'withdrawn' && <X className="w-3 h-3 mr-1" />}
                          {item.status === 'completed' && <CheckCircle className="w-3 h-3 mr-1" />}
                          {item.status.includes('rejected') && <XCircle className="w-3 h-3 mr-1" />}
                          {item.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                        <span className="text-sm text-gray-500 capitalize">
                          {item.resignationType.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Submitted:</span>{' '}
                          <span className="font-medium">{new Date(item.submittedDate).toLocaleDateString()}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">LWD Requested:</span>{' '}
                          <span className="font-medium">{new Date(item.lastWorkingDate).toLocaleDateString()}</span>
                        </div>
                        {item.status === 'withdrawn' && item.withdrawnDate && (
                          <div>
                            <span className="text-gray-500">Withdrawn:</span>{' '}
                            <span className="font-medium">{new Date(item.withdrawnDate).toLocaleDateString()}</span>
                          </div>
                        )}
                        {item.adminAction?.finalLastWorkingDate && item.status !== 'withdrawn' && (
                          <div>
                            <span className="text-gray-500">Final LWD:</span>{' '}
                            <span className="font-medium">{new Date(item.adminAction.finalLastWorkingDate).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                      {item.reason && (
                        <p className="text-sm text-gray-600 mt-2">
                          <span className="text-gray-500">Reason:</span> {item.reason}
                        </p>
                      )}
                      {item.status === 'withdrawn' && item.withdrawnReason && (
                        <p className="text-sm text-gray-600 mt-1">
                          <span className="text-gray-500">Withdraw Reason:</span> {item.withdrawnReason}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">My Resignation</h1>
        <p className="text-gray-600">Track your resignation request status</p>
      </div>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Status Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold mb-2">Resignation Status</h2>
              {getStatusBadge(myResignation.status)}
            </div>
            <Calendar className="w-12 h-12 opacity-50" />
          </div>
        </div>

        {/* Details */}
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-gray-500 mb-1">Resignation Type</p>
              <p className="font-medium text-gray-900 capitalize">
                {myResignation.resignationType.replace(/_/g, ' ')}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Submitted Date</p>
              <p className="font-medium text-gray-900">
                {new Date(myResignation.submittedDate).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Requested Last Working Date</p>
              <p className="font-medium text-gray-900">
                {new Date(myResignation.lastWorkingDate).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Notice Period</p>
              <p className="font-medium text-gray-900">{myResignation.noticePeriodDays} days</p>
            </div>
          </div>

          <div>
            <p className="text-sm text-gray-500 mb-1">Reason</p>
            <p className="text-gray-900">{myResignation.reason}</p>
          </div>

          {myResignation.feedback && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Feedback</p>
              <p className="text-gray-900">{myResignation.feedback}</p>
            </div>
          )}

          {/* Manager Action */}
          {myResignation.reporterAction.status !== 'not_applicable' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <Clock className="w-5 h-5 mr-2 text-blue-600" />
                Reporting Manager Review
              </h3>
              <div className="space-y-2">
                <p className="text-sm">
                  <span className="text-gray-600">Status:</span>{' '}
                  <span className="font-medium capitalize">{myResignation.reporterAction.status}</span>
                </p>
                {myResignation.reporterAction.actionBy && (
                  <>
                    <p className="text-sm">
                      <span className="text-gray-600">Reviewed By:</span>{' '}
                      <span className="font-medium">{myResignation.reporterAction.actionBy.name}</span>
                    </p>
                    <p className="text-sm">
                      <span className="text-gray-600">Date:</span>{' '}
                      <span className="font-medium">
                        {new Date(myResignation.reporterAction.actionDate).toLocaleDateString()}
                      </span>
                    </p>
                    {myResignation.reporterAction.comments && (
                      <p className="text-sm">
                        <span className="text-gray-600">Comments:</span>{' '}
                        <span className="font-medium">{myResignation.reporterAction.comments}</span>
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Admin Action */}
          {myResignation.adminAction.status !== 'pending' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                Admin Review
              </h3>
              <div className="space-y-2">
                <p className="text-sm">
                  <span className="text-gray-600">Status:</span>{' '}
                  <span className="font-medium capitalize">{myResignation.adminAction.status}</span>
                </p>
                {myResignation.adminAction.actionBy && (
                  <>
                    <p className="text-sm">
                      <span className="text-gray-600">Reviewed By:</span>{' '}
                      <span className="font-medium">{myResignation.adminAction.actionBy.name}</span>
                    </p>
                    {myResignation.adminAction.finalLastWorkingDate && (
                      <p className="text-sm">
                        <span className="text-gray-600">Final Last Working Date:</span>{' '}
                        <span className="font-medium">
                          {new Date(myResignation.adminAction.finalLastWorkingDate).toLocaleDateString()}
                        </span>
                      </p>
                    )}
                    {myResignation.adminAction.comments && (
                      <p className="text-sm">
                        <span className="text-gray-600">Comments:</span>{' '}
                        <span className="font-medium">{myResignation.adminAction.comments}</span>
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Timeline */}
          {myResignation.timeline && myResignation.timeline.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Timeline</h3>
              <div className="space-y-4">
                {myResignation.timeline.map((entry, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <Clock className="w-5 h-5 text-blue-600" />
                      </div>
                    </div>
                    <div className="flex-1 pb-4 border-b border-gray-200 last:border-0">
                      <p className="font-medium text-gray-900">{entry.action}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(entry.timestamp).toLocaleString()}
                      </p>
                      {entry.comments && (
                        <p className="text-sm text-gray-600 mt-1">{entry.comments}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          {['pending', 'approved_by_reporter'].includes(myResignation.status) && (
            <div className="pt-4 border-t">
              <button
                onClick={() => setShowWithdrawModal(true)}
                className="w-full bg-red-600 text-white py-3 px-6 rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Withdraw Resignation
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 z-50">
          <CheckCircle className="w-5 h-5" />
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="fixed bottom-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 z-50">
          <XCircle className="w-5 h-5" />
          {errorMessage}
        </div>
      )}

      {/* Withdrawal Confirmation Modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Withdraw Resignation</h2>
              <button
                onClick={() => {
                  setShowWithdrawModal(false);
                  setWithdrawReason('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-4">
                Are you sure you want to withdraw your resignation request? Please provide a reason below.
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Withdrawal <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={withdrawReason}
                  onChange={(e) => setWithdrawReason(e.target.value)}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Please explain why you want to withdraw your resignation..."
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowWithdrawModal(false);
                    setWithdrawReason('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={withdrawing}
                >
                  Cancel
                </button>
                <button
                  onClick={handleWithdraw}
                  disabled={withdrawing || !withdrawReason.trim()}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {withdrawing ? 'Withdrawing...' : 'Confirm Withdrawal'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeResignation;
